import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type FormEvent,
} from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "./useAuth";
import { usePublicBranding } from "./usePublicBranding";
import { useCustomDomainBranding } from "@/features/shell/useCustomDomainBranding";
import { getCustomDomainHost } from "@/features/shell/ThemeProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Shield, Loader2 } from "lucide-react";
import {
  getOIDCConfig,
  hasManualLogout,
  redirectToKeycloakLogin,
} from "./oidc";
import { useI18n } from "@/lib/i18n";

/** Build scoped CSS variables from branding_config.theme */
function buildBrandingStyles(
  theme: Record<string, string> | undefined,
): CSSProperties {
  if (!theme) return {};
  const vars: Record<string, string> = {};
  if (theme.brand_primary) vars["--brand-primary"] = theme.brand_primary;
  if (theme.accent_primary) vars["--brand-accent"] = theme.accent_primary;
  if (theme.accent_secondary)
    vars["--brand-accent-secondary"] = theme.accent_secondary;
  return vars;
}

export function LoginPage() {
  const { isAuthenticated, isLoading: authLoading, login } = useAuth();
  const { t } = useI18n();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const [searchParams] = useSearchParams();

  // Custom domain detection — fetch branding by domain when on a custom portal
  const customDomain = useMemo(() => getCustomDomainHost(), []);
  const { data: domainBrandingData, isLoading: domainBrandingLoading } =
    useCustomDomainBranding(customDomain);

  const {
    data: slugBranding,
    isLoading: slugBrandingLoading,
    isError: slugBrandingError,
  } = usePublicBranding(orgSlug);

  // Merge: domain branding takes priority over slug branding
  const branding = domainBrandingData ?? slugBranding;
  const brandingLoading = domainBrandingLoading || slugBrandingLoading;
  const brandingError = !domainBrandingData && slugBrandingError;
  const hasSlugOrDomain = !!orgSlug || !!customDomain;
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [manualLogout] = useState(() => hasManualLogout());
  const oidcConfig = getOIDCConfig();
  const wasLoggedOut = searchParams.get("logged_out") === "1";

  // Resolve branding: use fetched data or fall back to defaults
  const hasBranding = hasSlugOrDomain && !!branding && !brandingError;
  const orgName = hasBranding ? branding.name : "Sentinels Core";
  const logoUrl = hasBranding
    ? (branding.branding_config?.logos?.header ??
      branding.branding_config?.logos?.sidebar)
    : null;
  const theme = hasBranding
    ? (branding.branding_config?.theme as Record<string, string> | undefined)
    : undefined;
  const brandingStyles = buildBrandingStyles(theme);

  useEffect(() => {
    if (
      authLoading ||
      isAuthenticated ||
      (hasSlugOrDomain && brandingLoading)
    ) {
      return;
    }

    // In non-OIDC (dev) environments we render a username/password form
    // instead of auto-redirecting to Keycloak. Bailing out here keeps
    // production (OIDC enabled) strictly SSO-only — the form never renders.
    if (!oidcConfig.enabled) {
      return;
    }

    if (isSubmitting || (manualLogout && !wasLoggedOut)) return;

    setError(null);
    setIsSubmitting(true);
    if (hasBranding && branding?.id) {
      localStorage.setItem("active_organization_id", branding.id);
    }
    void redirectToKeycloakLogin(
      branding?.id,
      wasLoggedOut ? { prompt: "login" } : undefined,
    ).catch(() => {
      setError(t("oidc.signInFailed"));
      setIsSubmitting(false);
    });
  }, [
    authLoading,
    branding?.id,
    brandingLoading,
    hasBranding,
    hasSlugOrDomain,
    isAuthenticated,
    isSubmitting,
    manualLogout,
    oidcConfig.enabled,
    t,
    wasLoggedOut,
  ]);

  async function handleCredentialsSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      // Mirror the OIDC callback: `login` POSTs to /api/v1/auth/token/ through
      // the shared API client, persists the access token, and refreshes the
      // auth context (session bootstrap + /auth/me/). On success
      // `isAuthenticated` flips and the <Navigate> below redirects to "/".
      await login(username, password);
    } catch {
      setError(t("login.invalid"));
      setIsSubmitting(false);
    }
  }

  // Show spinner while checking auth state
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Show neutral loading while fetching branding (not default Sentinels)
  if (hasSlugOrDomain && brandingLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
      </div>
    );
  }

  return (
    <div
      className="bg-muted/40 flex min-h-screen flex-col items-center justify-center p-4"
      style={brandingStyles}
    >
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={`${orgName} logo`}
              className="mx-auto mb-2 h-12 max-w-[200px] object-contain"
              onError={(e) => {
                // Fall back to Sentinels icon on load error
                e.currentTarget.style.display = "none";
                const fallback = e.currentTarget.nextElementSibling;
                if (fallback instanceof HTMLElement) {
                  fallback.style.display = "flex";
                }
              }}
            />
          ) : null}
          <div
            className={`bg-primary mx-auto mb-2 h-12 w-12 items-center justify-center rounded-lg ${
              logoUrl ? "hidden" : "flex"
            }`}
          >
            <Shield className="text-primary-foreground h-6 w-6" />
          </div>
          <CardTitle
            className="text-2xl font-bold"
            style={
              theme?.brand_primary ? { color: theme.brand_primary } : undefined
            }
          >
            {orgName}
          </CardTitle>
          <CardDescription>{t("login.account")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {error && (
              <div className="bg-destructive/10 text-destructive rounded-md p-3 text-sm">
                {error}
              </div>
            )}
            {oidcConfig.enabled ? (
              <Button
                type="button"
                className="w-full"
                disabled={isSubmitting}
                onClick={() => {
                  setError(null);
                  setIsSubmitting(true);
                  if (hasBranding && branding?.id) {
                    localStorage.setItem("active_organization_id", branding.id);
                  }
                  void redirectToKeycloakLogin(branding?.id).catch(() => {
                    setError(t("oidc.signInFailed"));
                    setIsSubmitting(false);
                  });
                }}
                style={
                  theme?.accent_primary
                    ? {
                        backgroundColor: theme.accent_primary,
                        borderColor: theme.accent_primary,
                      }
                    : undefined
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("login.redirecting")}
                  </>
                ) : (
                  <>
                    <Shield className="mr-2 h-4 w-4" />
                    {t("login.keycloak")}
                  </>
                )}
              </Button>
            ) : (
              <form
                onSubmit={handleCredentialsSubmit}
                className="space-y-4"
                noValidate
              >
                <div className="space-y-2">
                  <Label htmlFor="username">{t("login.username")}</Label>
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t("login.password")}</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                  style={
                    theme?.accent_primary
                      ? {
                          backgroundColor: theme.accent_primary,
                          borderColor: theme.accent_primary,
                        }
                      : undefined
                  }
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("login.submitting")}
                    </>
                  ) : (
                    t("login.submit")
                  )}
                </Button>
              </form>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Always show "Powered by" footer */}
      <p className="mt-6 text-center text-xs text-neutral-400">
        {t("login.poweredBy")}
      </p>
    </div>
  );
}
